// src/features/scheduleGenerator/components/ScheduleItemCard.tsx
import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { TimeBox } from '../../../types/timeBox';
import { Event } from '../../../types/calendar-event';
import { timeBoxCategoryConfig } from '../../../constants/timebox.constants';
import TimeBoxPattern from '../../../components/common/TimeBoxPattern';
import { MapPin, ArrowUpDown, ChevronsUpDown, Trash2, Sun, Sunset, Moon, FoldVertical, UnfoldVertical, Lightbulb } from 'lucide-react';

// Timeline 風格時間箱卡片組件
export const TimelineTimeBoxCard: React.FC<{
    timeBox: TimeBox;
    timeBoxIndex: number; // editedTimeBoxes 陣列中的索引（不含 Event），與 sortedItems index 不同
    onDelete: (index: number) => void;
    onSelect?: (index: number) => void;
    isSelected?: boolean;
    onAICommand?: (command: string) => void;
}> = ({ timeBox, timeBoxIndex, onDelete, onSelect, isSelected = false, onAICommand }) => {
    const [actionMode, setActionMode] = useState<null | 'move' | 'resize'>(null);
    const [pendingDelete, setPendingDelete] = useState(false);
    const [customCommand, setCustomCommand] = useState('');
    const deleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const startTime = new Date(timeBox.startTime);
    const endTime = new Date(timeBox.endTime);

    useEffect(() => {
        if (!isSelected) {
            setActionMode(null);
            setPendingDelete(false);
            if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
        }
    }, [isSelected]);

    useEffect(() => {
        return () => { if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current); };
    }, []);

    const handleRemoveClick = () => {
        if (pendingDelete) {
            onDelete(timeBoxIndex);
            return;
        }
        setPendingDelete(true);
        setActionMode(null);
        deleteTimerRef.current = setTimeout(() => setPendingDelete(false), 2000);
    };

    const handleMoveOption = (period: 'morning' | 'afternoon' | 'evening') => {
        onAICommand?.(`Move "${timeBox.title}" to the ${period}`);
    };

    const handleResizeOption = (direction: 'shorter' | 'longer') => {
        const action = direction === 'shorter' ? 'shorten by 30 minutes' : 'extend by 30 minutes';
        onAICommand?.(`For "${timeBox.title}": ${action}`);
    };

    const handleCustomSubmit = () => {
        if (!customCommand.trim()) return;
        onAICommand?.(`For "${timeBox.title}": ${customCommand.trim()}`);
        setCustomCommand('');
    };

    return (
        <div className="relative">
            {/* 時間軸節點 - 移動版位置優化 */}
            <div className="absolute left-2 sm:left-4 top-6 z-10">
                <div className="w-4 h-4 rounded-full bg-white border-2 border-sky-300 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-sky-500"></div>
                </div>
            </div>

            {/* 時間箱卡片 - 移動版偏移量優化 */}
            <div className="ml-10 sm:ml-12">
                <div
                    className={`relative sm:p-4 p-3 bg-white border shadow-sm transition-all overflow-hidden group cursor-pointer ${
                        isSelected ? 'rounded-t-xl border-gray-200 shadow-md' : 'rounded-xl border-gray-200 hover:shadow-md'
                    }`}
                    onClick={() => onSelect?.(timeBoxIndex)}
                >
                    {/* 背景圖案 */}
                    <TimeBoxPattern category={timeBox.category} opacity={0.05} />

                    <div className="relative z-10">
                        <div className="flex items-start">
                            <div className="flex-1">
                                {/* 標題 */}
                                <h4 className="font-medium text-gray-900 mb-1">{timeBox.title}</h4>

                                {/* 時間 + 類別標籤 */}
                                <div className="flex items-center text-sm text-gray-600 mb-2">
                                    <span>{format(startTime, 'HH:mm')} - {format(endTime, 'HH:mm')}</span>
                                    <span className="mx-2">·</span>
                                    <span className={`
                                        px-2 py-0.5 rounded-full text-xs font-medium
                                        ${timeBoxCategoryConfig[timeBox.category]?.color || 'bg-slate-100 text-slate-900'}
                                    `}>
                                        {timeBoxCategoryConfig[timeBox.category]?.label || timeBox.category}
                                    </span>
                                </div>

                                {/* AI 排程理由（選中時展開，僅 Preview 暫態） */}
                                {timeBox.reasoning && (
                                    <div className="flex items-start gap-1.5 text-sm text-gray-400">
                                        <Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                        <span className={isSelected ? '' : 'line-clamp-1'}>
                                            {timeBox.reasoning}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 行內展開的操作列 */}
                <div
                    className={`grid transition-[grid-template-rows] duration-150 ease-out ${isSelected ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                    onClick={e => e.stopPropagation()}
                >
                    <div className={`overflow-hidden border border-t-0 border-gray-200 rounded-b-xl shadow-md ${isSelected ? '' : 'border-0 shadow-none'}`}>
                        <div className={`px-3 pb-3 pt-0 ${timeBoxCategoryConfig[timeBox.category]?.actionBg || 'bg-neutral-50'}`}>
                            {/* 分隔線 */}
                            <div className="border-t border-neutral-100 mb-2.5" />

                            {/* 快捷操作列 */}
                            <div className="flex gap-2 mb-2">
                                <button
                                    onClick={() => setActionMode(prev => prev === 'move' ? null : 'move')}
                                    className={`flex-1 py-3 sm:py-2 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                                        actionMode === 'move'
                                            ? 'bg-neutral-100 border-neutral-300 text-neutral-800'
                                            : 'bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                                    }`}
                                    aria-label="Move"
                                >
                                    <ArrowUpDown className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
                                    <span className="hidden sm:inline">Move</span>
                                </button>
                                <button
                                    onClick={() => setActionMode(prev => prev === 'resize' ? null : 'resize')}
                                    className={`flex-1 py-3 sm:py-2 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                                        actionMode === 'resize'
                                            ? 'bg-neutral-100 border-neutral-300 text-neutral-800'
                                            : 'bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                                    }`}
                                    aria-label="Resize"
                                >
                                    <ChevronsUpDown className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
                                    <span className="hidden sm:inline">Resize</span>
                                </button>
                                <button
                                    onClick={handleRemoveClick}
                                    className={`flex-1 py-3 sm:py-2 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                                        pendingDelete
                                            ? 'bg-red-500 border-red-500 text-white hover:bg-red-600'
                                            : 'bg-white border-neutral-200 text-red-500 hover:bg-red-50 hover:border-red-200'
                                    }`}
                                    aria-label={pendingDelete ? 'Confirm remove' : 'Remove'}
                                >
                                    <Trash2 className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
                                    <span className="hidden sm:inline">{pendingDelete ? 'Confirm?' : 'Remove'}</span>
                                </button>
                            </div>

                            {/* Move 子選項 */}
                            {actionMode === 'move' && (
                                <div className="flex gap-2 mb-2">
                                    {([
                                        { key: 'morning', Icon: Sun, label: 'Morning' },
                                        { key: 'afternoon', Icon: Sunset, label: 'Afternoon' },
                                        { key: 'evening', Icon: Moon, label: 'Evening' },
                                    ] as const).map(({ key, Icon, label }) => (
                                        <button
                                            key={key}
                                            onClick={() => handleMoveOption(key)}
                                            className="flex-1 py-2.5 sm:py-1.5 rounded-lg bg-white border border-neutral-300 text-sm text-neutral-700 font-medium hover:bg-neutral-50 transition-colors flex items-center justify-center gap-1.5"
                                            aria-label={label}
                                        >
                                            <Icon className="w-5 h-5 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                                            <span className="hidden sm:inline">{label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Resize 子選項 */}
                            {actionMode === 'resize' && (
                                <div className="flex gap-2 mb-2">
                                    {([
                                        { key: 'shorter', Icon: FoldVertical, label: 'Shorter' },
                                        { key: 'longer', Icon: UnfoldVertical, label: 'Longer' },
                                    ] as const).map(({ key, Icon, label }) => (
                                        <button
                                            key={key}
                                            onClick={() => handleResizeOption(key)}
                                            className="flex-1 py-2.5 sm:py-1.5 rounded-lg bg-white border border-neutral-300 text-sm text-neutral-700 font-medium hover:bg-neutral-50 transition-colors flex items-center justify-center gap-1.5"
                                            aria-label={label}
                                        >
                                            <Icon className="w-5 h-5 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                                            <span className="hidden sm:inline">{label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* 自訂 AI 指令 */}
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    placeholder="Custom instruction..."
                                    value={customCommand}
                                    onChange={e => setCustomCommand(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleCustomSubmit(); }}
                                    className="flex-1 px-3 py-1.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400 placeholder:text-neutral-300"
                                />
                                <button
                                    onClick={handleCustomSubmit}
                                    disabled={!customCommand.trim()}
                                    className="p-1.5 rounded-lg bg-neutral-800 text-white disabled:opacity-25 hover:bg-neutral-700 transition-colors"
                                    aria-label="Submit instruction"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

type TimeValue = string | number | Date | { toDate: () => Date } | null | undefined;

// 時區安全的時間轉換函數
export const getValidDate = (timeValue: TimeValue, baseDate?: string): Date | null => {
    if (!timeValue) return null;

    // 處理 Firestore Timestamp
    if (typeof timeValue === 'object' && !(timeValue instanceof Date) && 'toDate' in timeValue) {
        return timeValue.toDate();
    }

    // 處理純時間字符串格式 (如 '09:00')
    if (typeof timeValue === 'string' && /^\d{2}:\d{2}$/.test(timeValue) && baseDate) {
        try {
            const [hours, minutes] = timeValue.split(':').map(Number);
            const [year, month, day] = baseDate.split('-').map(Number);

            // 使用 Date 構造函數，確保本地時區
            const date = new Date(year, month - 1, day, hours, minutes, 0, 0);

            return isNaN(date.getTime()) ? null : date;
        } catch {
            return null;
        }
    }

    // 處理其他格式（string | number | Date）
    const date = new Date(timeValue as string | number | Date);
    return isNaN(date.getTime()) ? null : date;
};

// Timeline 風格事件卡片組件
export const TimelineEventCard: React.FC<{
    event: Event;
    index: number;
    baseDate?: string;
}> = ({ event, baseDate }) => {
    const isAllDay = event.timeType === 'allday';
    const startTime = getValidDate(event.startTime, baseDate);
    const endTime = event.endTime ? getValidDate(event.endTime, baseDate) : null;

    if (isAllDay) {
        return (
            <div className="relative">
                <div className="absolute left-2 sm:left-4 top-6 z-10">
                    <div className="w-4 h-4 rounded-full bg-white border-2 border-red-300 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    </div>
                </div>
                <div className="ml-10 sm:ml-12">
                    <div className="relative sm:p-4 p-3 rounded-xl bg-red-50 border border-red-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-red-900">{event.title}</h4>
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">All day</span>
                        </div>
                        {event.notes && (
                            <p className="text-sm text-red-600 italic">{event.notes}</p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (!startTime) {
        return null;
    }

    return (
        <div className="relative">
            {/* 時間軸節點 - 移動版位置優化 */}
            <div className="absolute left-2 sm:left-4 top-6 z-10">
                <div className="w-4 h-4 rounded-full bg-white border-2 border-red-300 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                </div>
            </div>

            {/* 事件卡片 - 移動版偏移量優化 */}
            <div className="ml-10 sm:ml-12">
                <div className="relative sm:p-4 p-3 rounded-xl bg-red-50 border border-red-200 shadow-sm">
                    <div className="relative z-10">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                {/* 事件標題 */}
                                <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-medium text-red-900">{event.title}</h4>
                                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                        Event
                                    </span>
                                </div>

                                {/* 時間 + 類別標籤 */}
                                <div className="flex items-center text-sm text-red-700 mb-2">
                                    <span>
                                        {format(startTime, 'HH:mm')}
                                        {endTime && ` - ${format(endTime, 'HH:mm')}`}
                                    </span>
                                    <span className="mx-2">·</span>
                                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        {event.category}
                                    </span>
                                </div>

                                {/* Description */}
                                {event.notes && (
                                    <p className="text-sm text-red-600 italic">
                                        {event.notes}
                                    </p>
                                )}

                                {/* 位置 */}
                                {event.location && (
                                    <p className="text-xs text-red-500 mt-1">
                                        <MapPin className="inline w-3 h-3 mr-1" /> {event.location}
                                    </p>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
